import { memo } from 'react';
import { MonitorUp, Volume2, VolumeX } from 'lucide-react';
import { Button, cn } from '@cyphex/ui';
import type { SharePresenter } from '../media/shareArbiter';

export interface PresenterBarProps {
  /** Who owns the stage, or null when nobody is presenting. */
  presenter: SharePresenter | null;
  isLocalPresenter: boolean;
  /** This client's own share is carrying system audio. */
  withAudio: boolean;
  /** The presenter turned on "Include computer sound" for this share. */
  soundRequested: boolean;
  onTakeOver: () => void;
  onStop: () => void;
}

/**
 * "X IS PRESENTING" — the line that makes one-presenter-at-a-time discoverable.
 *
 * Nothing anywhere used to say who was presenting. The share tile's nameplate is
 * deliberately hover-only (every pixel of a share is somebody's document), the
 * People panel had no share glyph at all, and the presence badge is never set
 * for a call — so a second presenter had no way to find out their screen was
 * reaching nobody, and a viewer had no way to find out whose screen they were
 * looking at. Both of those are answered here, in one line, in every layout.
 *
 * It also answers the question the presenter has: `active` on a Share button
 * only ever meant "my capture is running". This says whether the room can see
 * it, and whether it can hear it.
 */
export const PresenterBar = memo(function PresenterBar(props: PresenterBarProps) {
  const { presenter } = props;
  if (presenter === null) return null;

  const mine = props.isLocalPresenter;

  return (
    <div
      role="status"
      className={cn(
        'flex shrink-0 items-center justify-center gap-2 rounded-lg border border-border',
        'px-3 py-1.5 text-base text-secondary-foreground',
        mine ? 'bg-info-subtle' : 'bg-subtle',
      )}
    >
      <MonitorUp size={16} strokeWidth={2} aria-hidden />
      <span>
        {mine ? 'You are presenting to everyone.' : `${presenter.name} is presenting.`}
      </span>

      {/* Said only to the person who can act on it, and only when it is true.
          Sound is OFF unless the presenter turned it on in the picker, so its
          absence is news only when they asked for it: a browser hands back a
          tab's sound and never a whole screen's, and Safari none at all. That
          is the platform, not a fault — but a presenter playing a video to a
          silent room deserves to know before somebody has to tell them. */}
      {mine && props.soundRequested && !props.withAudio ? (
        <span className="flex items-center gap-1 text-muted-foreground">
          <VolumeX size={14} strokeWidth={2} aria-hidden />
          Computer sound is not included
        </span>
      ) : null}
      {/* And while it IS on, it stays in sight: on Windows it carries this call
          back to everybody, so it is something to stop presenting to end, not
          something to forget about. */}
      {mine && props.withAudio ? (
        <span className="flex items-center gap-1 text-muted-foreground">
          <Volume2 size={14} strokeWidth={2} aria-hidden />
          Sharing computer sound
        </span>
      ) : null}

      {mine ? (
        <Button variant="link" size="xs" onClick={props.onStop}>
          Stop presenting
        </Button>
      ) : (
        /* THE TAKE-OVER PATH, in the one place a person is already looking when
           they wonder why their own Share did nothing. Taking over stops the
           current presenter's share — it does not add a second one. */
        <Button variant="link" size="xs" onClick={props.onTakeOver}>
          Take over
        </Button>
      )}
    </div>
  );
});
